import React from 'react';
import 'twin.macro';
import { HeaderComponent } from '@teetsh/app/src/containers/Header';
import { TitleHelmet } from '@teetsh/app/src/components/TitleHelmet/TitleHelmet';
import { Outlet, useParams } from 'react-router-dom';
import { useSequence } from '@teetsh/app/src/queries/sequences/useSequence';
import { useUser } from '@teetsh/app/src/containers/App/context/useUser';
import useUpdateUser from '@teetsh/app/src/queries/user/useUpdateUser';
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from '@teetsh/app/src/components/Resizable';
import { Sidebar } from '@teetsh/app/src/containers/Sequences/Preview/Sidebar';
import { Body } from '@teetsh/app/src/containers/Sequences/Preview/Body';
import { Form } from 'react-final-form';
import AutoSubmitForm from '@teetsh/app/src/components/AutoSave';
import { useUserAccount } from '@teetsh/app/src/queries/user/account/useUserAccount';
import useFicheDePrep from '@teetsh/app/src/queries/fdp/useFicheDePrep';
import { useDownloadSequence } from '@teetsh/app/src/queries/sequences/downloadSequence';
import { useDownloadFicheDePrep } from '@teetsh/app/src/queries/fdp/downloadFicheDePrep';
import { FDPPDFSettings } from '@teetsh/app/src/types/sequences';

export function SequencePreviewWrapper() {
  return (
    <>
      <HeaderComponent />
      <TitleHelmet titleKey="sidebar.sequences" />
      <Outlet />
    </>
  );
}

export function SequencePreview() {
  const user = useUser();
  const updateUser = useUpdateUser();
  const { data: userAccount } = useUserAccount();
  const params = useParams<{
    sequenceId: string;
    ficheDePrepId?: string;
  }>();
  const sequenceId = params.sequenceId as string;
  const ficheDePrepId = params.ficheDePrepId as string;

  const { data: sequence, error } = useSequence(sequenceId);
  const { data: ficheDePrep } = useFicheDePrep(sequenceId, ficheDePrepId);

  const { download: downloadSequence, isDownloading: isDownloadingSequence } =
    useDownloadSequence();

  const {
    download: downloadFicheDePrep,
    isDownloading: isDownloadingFicheDePrep,
  } = useDownloadFicheDePrep();

  const isDownloading = isDownloadingFicheDePrep || isDownloadingSequence;
  if (!sequence || error) {
    return null;
  }

  const download = () => {
    if (ficheDePrep) {
      return downloadFicheDePrep({
        ficheDePrep,
        userId: user.id,
        settings: user.fonts.fdp,
      });
    }
    if (sequence) {
      return downloadSequence({
        sequence,
        settings: user.fonts.fdp,
      });
    }
    return Promise.resolve();
  };

  return (
    <Form<FDPPDFSettings>
      initialValues={user.fonts.fdp}
      onSubmit={async values => {
        await updateUser({
          ...user,
          fonts: {
            ...user.fonts,
            fdp: values,
          },
        });
        return {};
      }}
    >
      {({ handleSubmit }) => (
        <form tw="min-h-0 h-full" onSubmit={handleSubmit}>
          <AutoSubmitForm />
          <div tw="w-full h-full flex flex-col overflow-hidden">
            <ResizablePanelGroup
              direction="horizontal"
              autoSaveId="sequence-preview-sidebar"
            >
              <Sidebar
                onDownload={() => download()}
                isDownloading={isDownloading}
                isSequence={ficheDePrepId === undefined}
                ficheDePrepId={ficheDePrepId}
                limitedBgSet={userAccount?.isOnOldPricing || false}
              />
              <ResizableHandle />
              <ResizablePanel order={1}>
                <div tw="h-full overflow-auto">
                  <Body
                    userId={user.id}
                    sequenceId={sequenceId}
                    ficheDePrepId={ficheDePrepId}
                  />
                </div>
              </ResizablePanel>
            </ResizablePanelGroup>
          </div>
        </form>
      )}
    </Form>
  );
}
